import { message as toaster } from 'antd';
import { API } from 'api';
import { getSignedUrl } from './commonSlice';

/**
 * Warehousing V2
 *
 * Warehousing introduces no new REST routes — it plugs into the generic V2 dispatch
 * endpoints (/create, /update, /search, /dashboard, /bulk-upload, /report) via new
 * discriminator keys.
 *
 * Envelope rules, taken from warehouse_controller.py rather than the draft design doc
 * (they differ):
 *   - /dashboard and /search handlers read `filter_by`
 *   - /create, /update and /bulk-upload handlers read `data`
 *   - /report reads `data.filter_by`
 *
 * `login_user_id` is NOT sent: lib/utils.validate_token decodes the bearer token and
 * injects it into whichever of `data` / `filter_by` is present.
 */

const API_VERSION = 'v2';

const DASHBOARD_URL = '/dashboard';
const SEARCH_URL = '/search';
const CREATE_URL = '/create';
const UPDATE_URL = '/update';
const BULK_UPLOAD_URL = '/bulk-upload';
const REPORT_URL = '/report';

export const WAREHOUSE_UPLOAD_MANPOWER = 'warehouse_manpower_csv';
export const WAREHOUSE_UPLOAD_SHIPMENT = 'warehouse_shipment_csv';
export const WAREHOUSE_UPLOAD_BAG = 'warehouse_bag_csv';

// S3 prefixes the bulk-upload service reads from (`{entity}/{key}`)
export const WAREHOUSE_ENTITY_MANPOWER = 'warehouse_manpower_upload';
export const WAREHOUSE_ENTITY_SHIPMENT = 'warehouse_shipment_upload';
export const WAREHOUSE_ENTITY_BAG = 'warehouse_bag_upload';

// ─────────────────────────────────────────────────────────────────────────────
// Dashboards
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Portfolio summary across all active warehousing projects.
 * @param {string} [period_month] - 'YYYY-MM' or 'YYYY-MM-DD'; defaults to current month
 * @returns {{ period_month, portfolio: {...}, projects: [...] }}
 */
export const getWarehousePortfolioV2 =
	({ period_month, showLoading = true } = {}) =>
	async () => {
		const CREDENTIALS = {
			url: DASHBOARD_URL,
			data: {
				dashboard_type: 'warehouse_portfolio',
				filter_by: period_month ? { period_month } : {},
			},
			version: API_VERSION,
			showLoading,
		};

		return API.common(CREDENTIALS).then((res) => res?.data || null);
	};

/**
 * Per-project MTD figures plus the fixed and monthly cost head lists.
 * @returns project figures — revenue / cost_excl_monthly / cost_incl_monthly /
 *          cost_actual / margin_abs / margin_pct / fixed_costs[] / monthly_costs[]
 */
export const getWarehouseProjectV2 =
	({ project_id, period_month, showLoading = true }) =>
	async () => {
		const CREDENTIALS = {
			url: DASHBOARD_URL,
			data: {
				dashboard_type: 'warehouse_project',
				filter_by: { project_id, period_month },
			},
			version: API_VERSION,
			showLoading,
		};

		return API.common(CREDENTIALS).then((res) => res?.data || null);
	};

// ─────────────────────────────────────────────────────────────────────────────
// Daily entries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Daily entries grouped by date. Returns manpower *and* shipment line items in one
 * response — each line item carries a `type` discriminator; there is no server-side
 * entry_type filter.
 * @param {Object} filter_by - { project_id, from_date, to_date }
 */
export const searchWarehouseDailyEntriesV2 =
	({ filter_by, showLoading = true }) =>
	async () => {
		const CREDENTIALS = {
			url: SEARCH_URL,
			data: { search_type: 'warehouse_daily_entries', filter_by },
			version: API_VERSION,
			showLoading,
		};

		return API.common(CREDENTIALS).then((res) => res?.data || null);
	};

/** The project's manpower type catalog, seeded at project creation. */
export const searchWarehouseManpowerTypesV2 =
	({ project_id, showLoading = false }) =>
	async () => {
		const CREDENTIALS = {
			url: SEARCH_URL,
			data: { search_type: 'warehouse_manpower_types', filter_by: { project_id } },
			version: API_VERSION,
			showLoading,
		};

		return API.common(CREDENTIALS).then((res) => res?.data?.manpower_types || []);
	};

/** Remove one daily line item. Hard delete server-side; the audit log retains it. */
export const deleteWarehouseDailyEntryV2 =
	({ project_id, entry_type, id }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: {
				update_type: 'delete_warehouse_daily_entry',
				data: { project_id, entry_type, id },
			},
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Entry deleted');
			return res;
		});
	};

/**
 * Correct a recorded shipment or bag count in place.
 *
 * entry_type is 'shipment' or 'bag' and `id` is the line item's own id, so the row
 * keeps its source and upload_batch_id instead of being deleted and re-added.
 * Manpower is not editable this way: it carries cost/headcount/hours, not one count.
 */
export const updateWarehouseDailyCountV2 =
	({ project_id, entry_type, id, count }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: {
				update_type: 'update_warehouse_daily_count',
				data: { project_id, entry_type, id, count },
			},
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success(entry_type === 'bag' ? 'Bag count updated' : 'Shipment count updated');
			return res;
		});
	};

export const createWarehouseDailyManpowerV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_daily_manpower', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Manpower entry added successfully');
			return res;
		});
	};

export const createWarehouseDailyShipmentV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_daily_shipment', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Shipment entry added successfully');
			return res;
		});
	};

/** Mirrors createWarehouseDailyShipmentV2 exactly — a third daily line item type. */
export const createWarehouseDailyBagV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_daily_bag', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Bag count entry added successfully');
			return res;
		});
	};

// ─────────────────────────────────────────────────────────────────────────────
// Cost heads
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a custom month-end cost head to the project's catalog.
 *
 * Defaults are seeded at project creation; this covers heads an operator needs later.
 * Returns the new `cost_head_id`, which the caller then uses to post a value.
 */
export const createWarehouseMonthlyCostHeadV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_monthly_cost_head', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => res?.data || null);
	};

export const createWarehouseMonthlyCostEntryV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_monthly_cost_entry', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Monthly cost saved successfully');
			return res;
		});
	};

/**
 * Correct the value on a month-end cost entry that already has one.
 *
 * Finance/Admin only (406 otherwise), and `reason` is mandatory — it is the only
 * record on the edit log of why a closed month's cost moved, so the UI must collect
 * it rather than offer it. Refused once that month's Cost is frozen.
 */
export const updateWarehouseMonthlyCostEntryV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: { update_type: 'warehouse_monthly_cost_entry', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Monthly cost updated successfully');
			return res?.data || null;
		});
	};

/** Remove a month-end cost entry. Same Finance/Admin gate and mandatory `reason`. */
export const deleteWarehouseMonthlyCostEntryV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: { update_type: 'delete_warehouse_monthly_cost_entry', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Monthly cost deleted successfully');
			return res?.data || null;
		});
	};

/**
 * Edit an existing project's revenue terms — slab rows, cost-plus margin %, and the
 * Cross Dock rate. `reason` is mandatory (the server 422s without one) and is what the
 * project's revenue edit log records.
 *
 * Not versioned server-side: revenue is recomputed from the live model every time a
 * dashboard is read, so a change here also moves already-reported months. That is why
 * the reason is required rather than optional.
 */
export const updateWarehouseRevenueModelV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: { update_type: 'warehouse_revenue_model', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Warehouse information updated successfully');
			return res?.data || null;
		});
	};

/**
 * Fixed cost heads are versioned — this closes the current version and opens a new one
 * effective today. The response carries the new `effective_from`.
 *
 * `reason` is mandatory in `req_data` — the backend 422s a blank or missing one, since it
 * is the only record on the audit log of why a fixed cost changed.
 */
export const updateWarehouseFixedCostV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: { update_type: 'warehouse_fixed_cost', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Fixed cost updated successfully');
			return res;
		});
	};

/**
 * Remove a fixed cost head from today onward.
 *
 * The row is not erased: the backend closes the current version so past months keep
 * costing what they actually cost. `reason` is mandatory and lands on the audit log.
 */
export const deleteWarehouseFixedCostV2 =
	({ project_id, cost_head_code, reason }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: {
				update_type: 'delete_warehouse_fixed_cost',
				data: { project_id, cost_head_code, reason },
			},
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => res);
	};

// ─────────────────────────────────────────────────────────────────────────────
// Revenue adjustments and freeze
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a one-off "Additional Revenue Head" (addition or deduction) against a project's
 * revenue for a given period. Purely provisional bookkeeping — it layers on top of the
 * base revenue model and additional charges, it does not change either of those.
 */
export const createWarehouseRevenueAdjustmentV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: CREATE_URL,
			data: { create_type: 'warehouse_revenue_adjustment', data: req_data },
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Revenue adjustment added successfully');
			return res?.data || null;
		});
	};

/**
 * Engage one of a project's two independent freeze locks (`revenue` or `cost`) for ONE
 * month (`period_month`, 'YYYY-MM' or 'YYYY-MM-DD'). Freezing September blocks only
 * mutations that fall in September — every other month on the same project stays fully
 * editable, so this must always be called with whichever month is currently selected in
 * the UI, never an implicit "now".
 *
 * One-way: a freeze is final and the server rejects `unfreeze` outright, so `action` is
 * always 'freeze'. Every mutation on the locked side, for that month, then 422s with a
 * clear message, which surfaces through the existing toast/error pattern.
 */
export const setWarehouseFreezeV2 =
	({ project_id, lock, action, period_month, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: UPDATE_URL,
			data: {
				update_type: 'warehouse_freeze',
				data: { project_id, lock, action, period_month },
			},
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => {
			const label = lock === 'revenue' ? 'Revenue' : 'Cost';
			const monthLabel = res?.data?.period_month || period_month;
			toaster.success(`${label} frozen for ${monthLabel} — this cannot be undone`);
			return res?.data || null;
		});
	};

// ─────────────────────────────────────────────────────────────────────────────
// CSV upload — signed URL to S3 first, then hand the key to /bulk-upload
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upload a manpower or shipment CSV.
 *
 * The bulk-upload handler does not accept a file body: the browser PUTs the CSV to S3
 * using a signed URL, then sends `{ project_id, key, entity }`. The service reads
 * `{entity}/{key}` back out of the bucket.
 *
 * Partial success is normal — the response reports `success_count` / `error_count` with
 * a per-row `errors[]`. A 422 means the file or its header was rejected outright and no
 * rows were persisted.
 */
export const uploadWarehouseCsvV2 =
	({ upload_type, entity, project_id, file, setErrors }) =>
	async (dispatch) => {
		const signedUrlResponse = await dispatch(
			getSignedUrl({
				url_type: 'upload',
				data: { entity, keys: [file.name] },
			})
		);

		const signedUrlData = signedUrlResponse?.data?.[0];
		if (!signedUrlData?.url) {
			throw new Error('Failed to get an upload URL');
		}

		const uploadResponse = await fetch(signedUrlData.url, { method: 'PUT', body: file });
		if (!uploadResponse.ok) {
			throw new Error('Failed to upload the file to storage');
		}

		const CREDENTIALS = {
			url: BULK_UPLOAD_URL,
			data: {
				upload_type,
				data: { project_id, key: signedUrlData.filename || file.name, entity },
			},
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS).then((res) => res?.data || null);
	};

// ─────────────────────────────────────────────────────────────────────────────
// Export
// ─────────────────────────────────────────────────────────────────────────────

/** Export the project's daily entries for a date range as CSV. */
export const exportWarehouseReportV2 =
	({ project_id, from_date, to_date }) =>
	async () => {
		const CREDENTIALS = {
			url: REPORT_URL,
			data: {
				report_type: 'warehouse_export',
				file_type: 'csv',
				data: { filter_by: { project_id, from_date, to_date } },
			},
			version: API_VERSION,
			showLoading: true,
		};

		return API.common(CREDENTIALS).then((res) => {
			const downloadUrl = res?.data?.download_url || res?.data?.url;
			if (downloadUrl) {
				window.open(downloadUrl, '_blank', 'noopener');
			} else {
				toaster.error('Export did not return a download link');
			}
			return res;
		});
	};
